import { useState, useRef, useCallback, useEffect } from 'react';

import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

interface UseDraftCommentBoxProps {
	expanded: boolean;
	isEditorEmpty: boolean;
}

export const useDraftCommentBox = ({ expanded, isEditorEmpty }: UseDraftCommentBoxProps) => {
	const pageMode = useGetPageMode();
	const [shouldShowDraftBox, setShouldShowDraftBox] = useState<boolean>(false);
	const [lastWrapperTop, setLastWrapperTop] = useState<number | null>(null);
	const expandedRef = useRef<boolean>(false);
	const isEditorEmptyRef = useRef<boolean>(true);

	const hideDraftBox = useCallback(() => {
		setShouldShowDraftBox(false);
	}, []);

	// Update refs when props change
	useEffect(() => {
		expandedRef.current = expanded;
	}, [expanded]);

	useEffect(() => {
		isEditorEmptyRef.current = isEditorEmpty;
	}, [isEditorEmpty]);

	// Custom hook to check if draft box should be shown
	const checkShouldShowDraftBox = useCallback(
		(
			wrapperVisibleHeight: number,
			buttonHeight: number,
			isWrapperTopIncreasing: boolean,
			currentLastWrapperTop: number | null,
		) => {
			const isWrapperVisibleHeightLessThanButtonHeight = wrapperVisibleHeight < buttonHeight;

			// Only trigger when expanded AND editor is not empty AND (top is increasing OR first scroll) AND visible height is less than or equal to button height
			return (
				expandedRef.current &&
				!isEditorEmptyRef.current &&
				(isWrapperTopIncreasing || currentLastWrapperTop === null) &&
				isWrapperVisibleHeightLessThanButtonHeight
			);
		},
		[],
	); // No dependencies needed since we're using refs

	// Add scroll event listener
	useEffect(() => {
		// Early return if feature is disabled
		if (!fg('confluence_comment_sticky_footer_enabled')) {
			return;
		}

		const handleScroll = () => {
			// Get the elements
			const addCommentButton = document.getElementById('addCommentButton');
			const objectCommentWrapper = document.querySelector('[data-testid="object-comment-wrapper"]');

			if (addCommentButton && objectCommentWrapper) {
				const buttonRect = addCommentButton.getBoundingClientRect();
				const wrapperRect = objectCommentWrapper.getBoundingClientRect();

				// Calculate wrapper's visible height using window.innerHeight - wrapperRect.top
				const wrapperVisibleHeight = Math.max(0, window.innerHeight - wrapperRect.top) + 6;
				const buttonHeight = buttonRect.height;
				const currentWrapperTop = wrapperRect.top;

				// Initialize lastWrapperTop if it's null (first scroll event)
				if (lastWrapperTop === null) {
					setLastWrapperTop(currentWrapperTop);
				}

				// Check if wrapper top is increasing (scrolling down)
				const isWrapperTopIncreasing =
					lastWrapperTop !== null && currentWrapperTop > lastWrapperTop;

				// Use the callback to check if draft box should be shown
				if (
					checkShouldShowDraftBox(
						wrapperVisibleHeight,
						buttonHeight,
						isWrapperTopIncreasing,
						lastWrapperTop,
					)
				) {
					setShouldShowDraftBox(true);
				}

				// Update last wrapper top for next comparison
				setLastWrapperTop(currentWrapperTop);
			}
		};

		// Add scroll listener to the appropriate scrollable container based on pageMode
		let scrollableContainer: Element | null = null;
		if (pageMode === PageMode.LIVE) {
			scrollableContainer = document.querySelector('[data-testid="editor-content-container"]');
		} else if (pageMode === PageMode.VIEW) {
			scrollableContainer = document.getElementById('AkMainContent');
		}
		if (scrollableContainer) {
			scrollableContainer.addEventListener('scroll', handleScroll, { passive: true });
		}

		return () => {
			// Clean up scrollable container listener
			let scrollableContainer: Element | null = null;
			if (pageMode === PageMode.LIVE) {
				scrollableContainer = document.querySelector('[data-testid="editor-content-container"]');
			} else if (pageMode === PageMode.VIEW) {
				scrollableContainer = document.getElementById('AkMainContent');
			}

			if (scrollableContainer) {
				scrollableContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, [lastWrapperTop, checkShouldShowDraftBox, pageMode]);

	// Early return if feature is disabled
	if (!fg('confluence_comment_sticky_footer_enabled')) {
		return {
			shouldShowDraftBox: false,
			hideDraftBox,
		};
	}

	return {
		shouldShowDraftBox,
		hideDraftBox,
	};
};
